import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ShoppingList } from '../types/ShoppingList';
import { apiFetch } from '../apiFetch';

interface PausedShoppingTripsModalProps {
  open: boolean;
  onClose: () => void;
  onSelectTrip: (list: ShoppingList) => void;
}

const PausedShoppingTripsModal: React.FC<PausedShoppingTripsModalProps> = ({
  open,
  onClose,
  onSelectTrip,
}) => {
  const [pausedLists, setPausedLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchPausedLists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
      const paused = (data as ShoppingList[]).filter(
        (list) => list.status === 'paused'
      );
      setPausedLists(paused);
    } catch (err: any) {
      setError(err.message || 'Failed to load paused shopping trips');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (open) {
      fetchPausedLists();
    }
  }, [open, fetchPausedLists]);

  const handleSelect = (list: ShoppingList) => {
    onSelectTrip(list);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PauseCircleOutlineIcon />
          Paused Shopping Trips
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select one to resume shopping
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : pausedLists.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No paused shopping trips found.
          </Typography>
        ) : (
          <List>
            {pausedLists.map((list) => (
              <ListItemButton
                key={list.id}
                onClick={() => handleSelect(list)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <ListItemIcon>
                  <StorefrontIcon />
                </ListItemIcon>
                <ListItemText
                  primary={list.name}
                  secondary={list.shop_name || 'No store assigned'}
                />
                {(list.picked_count ?? 0) > 0 && (
                  <Chip
                    label={`${list.picked_count} picked`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PausedShoppingTripsModal;
